import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import Card from "react-bootstrap/Card";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { getFieldValue } from "../scripts/formScriptsv2";
import { getSubmissionsByContainerIdForPrimaryObjectId } from "../scripts/formsyv2Scripts";
import { getFieldDate, getFieldName } from "../scripts/helperScripts";
import EntityHeader from "./common/EntityHeader";
import FieldDisplay from "./common/FieldDisplay";
import { getFPInvestigations } from "./services/soar";

function FPInvestigations({ fosterHome }) {
  const location = useLocation();
  let { fosterhomeid } = useParams();
  const [investigations, setInvestigations] = useState([]);
  const [formsyInvestigations, setFormsyInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [controllerSignal, setControllerSignal] = useState();
  const [includesSignatureNeeded, setIncludesSignatureNeeded] = useState(false);
  const navigate = useNavigate();
  const reportOrg =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "SOARLive"
      : "SOARD365Base";

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => {
      controller.abort();
    };
  }, []);

  const params = useParams();
  if (!fosterhomeid) {
    if (params.id) fosterhomeid = params.id;
  }

  useEffect(() => {
    if (fosterhomeid && controllerSignal && !controllerSignal.aborted) {
      const loadSubmissions = async () => {
        const responses = await Promise.all([
          getFPInvestigations(fosterhomeid),
          getSubmissionsByContainerIdForPrimaryObjectId(
            "EC878651-B6CF-EF11-88D0-F28FA7FAF263", // SAFY-FH-Rules Container Id
            fosterhomeid
          ),
        ]);

        if (!controllerSignal.aborted) {
          setInvestigations(responses[0]);

          if (responses[1]) {
            responses[1].sort((a, b) => {
              let aDate = DateTime.fromISO(
                a.Results.DateOfViolation.value
              ).toMillis();
              let bDate = DateTime.fromISO(
                b.Results.DateOfViolation.value
              ).toMillis();

              if (bDate > aDate) return 1;
              if (bDate < aDate) return -1;

              aDate = DateTime.fromISO(a.CreatedOn).toMillis();
              bDate = DateTime.fromISO(b.CreatedOn).toMillis();

              if (bDate > aDate) return 1;
              if (bDate < aDate) return -1;
              return 0;
            });
            setFormsyInvestigations(responses[1]);
          }

          setLoading(false);
        }
      };

      loadSubmissions();
    }
  }, [fosterhomeid, controllerSignal]);

  const addNewInvestigation = () => {
    const url =
      process.env.REACT_APP_ORG +
      `/main.aspx?etc=10539&extraqs=%3f_CreateFromId%3d${fosterHome.ccx_fostercarehomeid}%26_CreateFromType%3d10221%26etc%3d10539&histKey=599033237&newWindow=true&pagetype=entityrecord`;

    window.open(url, "FPRulesInvestigation");
  };

  const filteredFormsyInvestigations = () => {
    if (includesSignatureNeeded) {
      return formsyInvestigations.filter(
        (submission) => submission.FinalSigned === false
      );
    } else {
      return formsyInvestigations;
    }
  };

  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  return (
    <>
      {!loading && (
        <>
          {getFieldName(fosterHome.owningbusinessunit) === "SAFY of Kentucky" ||
          safyInfo.user.modules.includes("Admin") ? (
            <EntityHeader
              add={() => navigate("./rules", { state: { ...location.state } })}
              title={
                <div className="d-flex">
                  <div>
                    {filteredFormsyInvestigations().length > 0
                      ? "Rules Investigations"
                      : `No Rules Investigations${
                          includesSignatureNeeded
                            ? " Requiring Final Signature"
                            : ""
                        }`}
                  </div>
                  <div className="ms-5 ps-2 d-flex align-items-center">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="needsSignatureOnly"
                        checked={includesSignatureNeeded}
                        onChange={({ currentTarget }) => {
                          setIncludesSignatureNeeded(currentTarget.checked);
                        }}
                      />
                    </div>
                    <label
                      className="form-check-label h6 mb-0"
                      htmlFor="needsSignatureOnly"
                    >
                      Requires Final Signature
                    </label>
                  </div>
                </div>
              }
              body={
                <div className="mb-2">
                  {filteredFormsyInvestigations().map((submission) => (
                    <div className="p-2" key={submission.SubmissionId}>
                      <Card className="card-safy">
                        <Card.Header
                          className="p-0 ps-2 bg-safy1-1"
                          style={{ color: "white" }}
                        >
                          <div className="row">
                            <div className="col-10 ps-4 pt-1">
                              {getFieldValue(submission.Results.FHName)}
                              {submission.Results.DateOfViolation.value
                                ? ` - ${DateTime.fromISO(
                                    submission.Results.DateOfViolation.value
                                  ).toLocaleString(DateTime.DATE_SHORT)}`
                                : ""}
                            </div>
                            <div className="col-2">
                              <Link
                                to={`./rules/${submission.SubmissionId}`}
                                state={{ ...location.state }}
                              >
                                <i className="fas fa-pen text-white float-end p-2"></i>
                              </Link>
                            </div>
                          </div>
                        </Card.Header>
                        <Card.Body className="p-2 card-body-safy">
                          <FieldDisplay
                            label="Category"
                            value={getFieldValue(submission.Results.Category)}
                          />
                          <FieldDisplay
                            label="Date of Violation"
                            value={
                              submission.Results.DateOfViolation.value
                                ? DateTime.fromISO(
                                    submission.Results.DateOfViolation.value
                                  ).toLocaleString(DateTime.DATE_SHORT)
                                : ""
                            }
                          />
                          <FieldDisplay
                            label="SAFY Made Aware"
                            value={
                              submission.Results.SAFYAwareDate?.value
                                ? DateTime.fromISO(
                                    submission.Results.SAFYAwareDate.value
                                  ).toLocaleString(DateTime.DATE_SHORT)
                                : ""
                            }
                          />
                          <FieldDisplay
                            label="Home Status"
                            value={getFieldValue(submission.Results.Status)}
                          />
                          <FieldDisplay
                            label="Impact on Home"
                            value={getFieldValue(submission.Results.Impact)}
                          />
                          <FieldDisplay
                            label="External Investigation"
                            value={getFieldValue(
                              submission.Results.Investigation
                            )}
                          />
                          <FieldDisplay
                            label="Date Foster Home Rules Investigation Closed"
                            value={
                              submission.Results.InvestigationClosedDate?.value
                                ? DateTime.fromISO(
                                    submission.Results.InvestigationClosedDate
                                      .value
                                  ).toLocaleString(DateTime.DATE_SHORT)
                                : ""
                            }
                          />
                        </Card.Body>
                      </Card>
                    </div>
                  ))}
                </div>
              }
            ></EntityHeader>
          ) : null}
          {(process.env.REACT_APP_ENVIRONMENT !== "Production" &&
            getFieldName(fosterHome.owningbusinessunit) !==
              "SAFY of Kentucky") ||
          process.env.REACT_APP_ENVIRONMENT === "Production" ||
          investigations.length > 0 ? (
            <EntityHeader
              title={
                investigations.length > 0
                  ? "SOAR Rules Investigations"
                  : "No SOAR Rules Investigations"
              }
              add={
                getFieldName(fosterHome.owningbusinessunit) !==
                "SAFY of Kentucky"
                  ? addNewInvestigation
                  : null
              }
              body={
                <>
                  {investigations.map((investigation) => (
                    <div
                      className="p-2"
                      key={investigation.sor_fprulesinvestigationid}
                    >
                      <Card className="card-safy">
                        <Card.Header
                          className="p-0 ps-2 bg-safy1-1"
                          style={{ color: "white" }}
                        >
                          <div className="row">
                            <div className="col-10 ps-4 pt-1">
                              {investigation.sor_name}
                            </div>
                            <div className="col-2">
                              <i
                                className="fas fa-pen float-end p-2"
                                onClick={() => {
                                  const url =
                                    process.env.REACT_APP_ORG +
                                    "/main.aspx?etc=10539&extraqs=%3f_gridType%3d10539%26etc%3d10539%26id=" +
                                    investigation.sor_fprulesinvestigationid +
                                    "&pagetype=entityrecord";
                                  window.open(url, "FosterHome");
                                }}
                              ></i>
                              <i
                                className="fas fa-print float-end p-2"
                                onClick={() => {
                                  const url = `https://safyreports.azurewebsites.net/Reports/Incident/RulesInvestigation/Default?db=${reportOrg}&i=${investigation.sor_fprulesinvestigationid}`;
                                  window.open(
                                    url,
                                    "Foster Home Rules Investigation Report"
                                  );
                                }}
                              ></i>
                            </div>
                          </div>
                        </Card.Header>
                        <Card.Body className="p-2 card-body-safy">
                          {investigation.sor_typeofreview && (
                            <FieldDisplay
                              label="Type of Review"
                              value={getFieldName(
                                investigation.sor_typeofreview
                              )}
                            />
                          )}
                          {investigation.sor_datetimeofviolation && (
                            <FieldDisplay
                              label="Date of Violation"
                              value={getFieldDate(
                                investigation.sor_datetimeofviolation
                              )}
                            />
                          )}
                          {investigation.sor_safymadeaware && (
                            <FieldDisplay
                              label="SAFY Made Aware"
                              value={getFieldDate(
                                investigation.sor_safymadeaware
                              )}
                            />
                          )}
                          {investigation.sor_placedonhold && (
                            <FieldDisplay
                              label="Home On Hold"
                              value={getFieldName(
                                investigation.sor_placedonhold
                              )}
                            />
                          )}
                          {investigation.sor_removedfromhome && (
                            <FieldDisplay
                              label="Child(ren) Removed"
                              value={getFieldName(
                                investigation.sor_removedfromhome
                              )}
                            />
                          )}
                          {investigation.sor_externalinvestigationinprocess && (
                            <FieldDisplay
                              label="External Investigation"
                              value={getFieldName(
                                investigation.sor_externalinvestigationinprocess
                              )}
                            />
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  ))}
                </>
              }
            ></EntityHeader>
          ) : null}
        </>
      )}
    </>
  );
}

export default FPInvestigations;
